import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { TrendingUp, Package, ShoppingBag, AlertTriangle, Calculator, Wallet, ArrowUpRight, Boxes, Share2, Copy, Rocket, CheckCircle2, Circle, ChevronRight, FileDown, Receipt, Landmark, Megaphone } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, Cell } from "recharts";
import api, { API } from "@/lib/api";
import { money } from "@/lib/orderStatus";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: async () => (await api.get("/dashboard/stats")).data });
  const { data: business } = useQuery({ queryKey: ["business"], queryFn: async () => (await api.get("/business")).data });
  const cur = stats?.currency || "XOF";

  const isNew = stats && stats.total_products === 0 && stats.total_orders === 0;

  return (
    <div className="px-4 pt-6">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#E85D04]">{t("dash.title")}</p>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">{business?.name || t("dash.myShop")}</h1>
        </div>
        <span className="h-11 w-11 rounded-xl bg-[#1B4332] text-white flex items-center justify-center font-display font-bold">
          {(user?.name || "S").slice(0, 1).toUpperCase()}
        </span>
      </motion.div>

      <ShareShop business={business} />

      <motion.button
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        data-testid="promo-assistant-card"
        onClick={() => navigate("/promouvoir")}
        className="w-full text-left rounded-2xl bg-[#E85D04] text-white p-4 mb-4 flex items-center gap-3 active:scale-[0.98] transition-transform shadow-lg shadow-[#E85D04]/20"
      >
        <span className="h-11 w-11 rounded-xl bg-white/20 flex items-center justify-center shrink-0"><Megaphone className="h-6 w-6" /></span>
        <span className="flex-1">
          <span className="block font-display font-extrabold leading-tight">Assistant Boutique Pro</span>
          <span className="block text-xs text-white/85 mt-0.5">Score, pubs IA et plus de ventes</span>
        </span>
        <ChevronRight className="h-5 w-5 text-white/80" />
      </motion.button>

      {isNew && <Onboarding stats={stats} business={business} />}

      {/* Net profit hero */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl bg-[#1B4332] p-6 text-white relative overflow-hidden mb-4"
      >
        <div className="absolute inset-0 fabric-grain opacity-20" />
        <div className="relative">
          <div className="flex items-center gap-2 text-white/80 text-sm font-medium">
            <Wallet className="h-4 w-4" /> {t("dash.netProfit")}
          </div>
          <p data-testid="net-profit" className="font-display text-4xl font-extrabold mt-2">{money(stats?.net_profit, cur)}</p>
          <div className="flex items-center gap-4 mt-4 text-sm">
            <span className="flex items-center gap-1 text-[#FFBA08] font-semibold">
              <ArrowUpRight className="h-4 w-4" /> {t("dash.margin")} {stats?.margin_pct ?? 0}%
            </span>
            <span className="text-white/70">{t("dash.revenue")}: {money(stats?.revenue, cur)}</span>
          </div>
        </div>
      </motion.div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Kpi testid="kpi-revenue" icon={TrendingUp} color="#E85D04" label={t("dash.kpiRevenue")} value={money(stats?.revenue, cur)} />
        <Kpi testid="kpi-cogs" icon={Calculator} color="#0077B6" label={t("dash.kpiCogs")} value={money(stats?.cogs, cur)} />
        <Kpi testid="kpi-stock-value" icon={Boxes} color="#2D6A4F" label={t("dash.kpiStockValue")} value={money(stats?.stock_value, cur)} />
        <Kpi testid="kpi-pending" icon={ShoppingBag} color="#FFBA08" label={t("dash.kpiPending")} value={stats?.pending_orders ?? 0} />
      </div>

      {/* Accounting breakdown + PDF export */}
      <div className="bg-white rounded-xl border border-black/5 shadow-sm p-5 mb-4" data-testid="accounting-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold flex items-center gap-2"><Landmark className="h-5 w-5 text-[#1B4332]" /> {t("dash.accounting")}</h3>
          <a href={`${API}/dashboard/report.pdf`} target="_blank" rel="noreferrer" data-testid="export-report-btn" className="h-9 px-3 rounded-xl bg-[#1B4332] text-white text-sm font-semibold flex items-center gap-1.5 active:scale-95 transition-transform">
            <FileDown className="h-4 w-4" /> PDF
          </a>
        </div>
        <Acct label={t("dash.taxesCollected")} value={money(stats?.taxes_collected, cur)} />
        <Acct label={t("dash.deliveryCollected")} value={money(stats?.delivery_collected, cur)} />
        <Acct label={t("dash.serviceFees")} value={`− ${money(stats?.service_fees, cur)}`} warn />
        <Acct label={t("dash.netProfitLabel")} value={money(stats?.net_profit, cur)} bold />
      </div>

      {/* mini chart */}
      <div className="bg-white rounded-xl border border-black/5 shadow-sm p-5 mb-4">
        <h3 className="font-display font-semibold mb-1">{t("dash.finBreakdown")}</h3>
        <p className="text-xs text-[#9c968a] mb-3">{t("dash.finSub")}</p>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={[
            { name: t("dash.chartRevenue"), val: stats?.revenue || 0, c: "#E85D04" },
            { name: t("dash.chartCost"), val: stats?.cogs || 0, c: "#0077B6" },
            { name: t("dash.chartProfit"), val: stats?.net_profit || 0, c: "#2D6A4F" },
          ]}>
            <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#5C574F" }} />
            <Tooltip formatter={(v) => money(v, cur)} cursor={{ fill: "#F9F6F0" }} />
            <Bar dataKey="val" radius={[8, 8, 0, 0]}>
              {["#E85D04", "#0077B6", "#2D6A4F"].map((c) => <Cell key={c} fill={c} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {stats?.low_stock > 0 && (
        <div className="bg-[#FFBA08]/15 border border-[#FFBA08]/40 rounded-xl p-4 flex items-center gap-3 mb-4" data-testid="low-stock-alert">
          <AlertTriangle className="h-5 w-5 text-[#B07d00]" />
          <p className="text-sm font-medium text-[#7a5800]">
            {t("dash.lowStock", { count: stats.low_stock })}
          </p>
        </div>
      )}

      <CostCalculator cur={cur} />
    </div>
  );
}

function Kpi({ icon: Icon, color, label, value, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-black/5 shadow-sm p-4"
      data-testid={testid}
    >
      <span className="h-9 w-9 rounded-lg flex items-center justify-center mb-2" style={{ background: `${color}1a` }}>
        <Icon className="h-5 w-5" style={{ color }} strokeWidth={2.4} />
      </span>
      <p className="text-xs text-[#9c968a] font-medium">{label}</p>
      <p className="font-display font-bold text-lg leading-tight mt-0.5">{value}</p>
    </motion.div>
  );
}

function CostCalculator({ cur = "XOF" }) {
  const { t } = useTranslation();
  const [f, setF] = useState({ merchandise: "", transport: "", hotel: "", other: "", units: "" });
  const [res, setRes] = useState(null);

  const calc = () => {
    const merchandise = +f.merchandise || 0;
    const transport = +f.transport || 0;
    const hotel = +f.hotel || 0;
    const other = +f.other || 0;
    const units = +f.units || 1;
    const logistics = transport + hotel + other;
    const total = merchandise + logistics;
    const unit = units ? total / units : 0;
    setRes({ logistics, total, unit, suggested: Math.round(unit * 1.4) });
  };

  return (
    <div className="bg-white rounded-xl border border-black/5 shadow-sm p-5 mt-4" data-testid="cost-calculator">
      <div className="flex items-center gap-2 mb-1">
        <Calculator className="h-5 w-5 text-[#E85D04]" />
        <h3 className="font-display font-semibold">{t("dash.costCalc")}</h3>
      </div>
      <p className="text-xs text-[#9c968a] mb-4">{t("dash.costCalcSub")}</p>
      <div className="grid grid-cols-2 gap-3">
        {[["merchandise", `${t("dash.merchandise")} (${cur})`], ["transport", t("dash.transport")], ["hotel", t("dash.hotel")], ["other", t("dash.other")], ["units", t("dash.units")]].map(([k, l]) => (
          <div key={k} className={k === "units" ? "col-span-2" : ""}>
            <label className="text-xs font-semibold text-[#5C574F]">{l}</label>
            <input
              data-testid={`calc-${k}`}
              type="number"
              value={f[k]}
              onChange={(e) => setF({ ...f, [k]: e.target.value })}
              className="w-full h-12 bg-[#F9F6F0] rounded-xl px-3 mt-1 outline-none border border-transparent focus:border-[#E85D04]"
            />
          </div>
        ))}
      </div>
      <button
        data-testid="calc-btn"
        onClick={calc}
        className="w-full h-12 mt-4 rounded-2xl bg-[#1B4332] text-white font-semibold active:scale-95 transition-transform"
      >
        {t("dash.calculate")}
      </button>
      {res && (
        <div className="mt-4 grid grid-cols-2 gap-3" data-testid="calc-result">
          <Result label={t("dash.totalCost")} value={money(res.total, cur)} />
          <Result label={t("dash.unitCost")} value={money(res.unit, cur)} highlight />
          <Result label={t("dash.logistics")} value={money(res.logistics, cur)} />
          <Result label={t("dash.suggestedPrice")} value={money(res.suggested, cur)} highlight />
        </div>
      )}
    </div>
  );
}

function Result({ label, value, highlight }) {
  return (
    <div className={`rounded-xl p-3 ${highlight ? "bg-[#E85D04]/10" : "bg-[#F9F6F0]"}`}>
      <p className="text-xs text-[#9c968a] font-medium">{label}</p>
      <p className={`font-display font-bold ${highlight ? "text-[#E85D04]" : "text-[#1A1A1A]"}`}>{value}</p>
    </div>
  );
}

function Acct({ label, value, bold, warn }) {
  return (
    <div className={`flex justify-between py-1.5 ${bold ? "border-t border-[#F0ece4] mt-1 pt-2" : ""}`}>
      <span className={`text-sm ${bold ? "font-display font-bold text-[#1A1A1A]" : "text-[#5C574F]"}`}>{label}</span>
      <span className={`text-sm ${bold ? "font-display font-bold text-[#2D6A4F]" : warn ? "text-[#D00000] font-semibold" : "font-semibold"}`}>{value}</span>
    </div>
  );
}

function ShareShop({ business }) {
  const { t } = useTranslation();
  if (!business) return null;
  const storeUrl = `${window.location.origin}/boutique/${business.slug}`;

  const copy = () => { navigator.clipboard.writeText(storeUrl); toast.success(t("dash.shopCopied")); };
  const share = () => {
    const msg = `Découvrez ma boutique ${business.name} en ligne 🛍️ Commandez directement ici : ${storeUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl bg-white border border-black/5 shadow-sm p-4 mb-4 flex items-center gap-3"
      data-testid="dashboard-share-shop"
    >
      <span className="h-11 w-11 rounded-xl bg-[#25D366]/15 flex items-center justify-center shrink-0">
        <Share2 className="h-5 w-5 text-[#1ca34d]" strokeWidth={2.4} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm leading-tight">{t("dash.shareShop")}</p>
        <p className="text-xs text-[#9c968a] truncate">{storeUrl}</p>
      </div>
      <button data-testid="dashboard-copy-link-btn" onClick={copy} className="h-10 w-10 rounded-xl bg-[#F9F6F0] flex items-center justify-center text-[#5C574F] active:scale-95 transition-transform">
        <Copy className="h-4 w-4" />
      </button>
      <button data-testid="dashboard-share-btn" onClick={share} className="h-10 px-4 rounded-xl bg-[#25D366] text-white font-semibold text-sm flex items-center gap-1.5 active:scale-95 transition-transform">
        <Share2 className="h-4 w-4" /> {t("dash.share")}
      </button>
    </motion.div>
  );
}

function Onboarding({ stats, business }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const steps = [
    { done: stats.total_products > 0, label: t("dash.stepProduct"), desc: t("dash.stepProductD"), to: "/stocks", testid: "onboarding-step-product" },
    { done: !!(business?.whatsapp), label: t("dash.stepWhatsapp"), desc: t("dash.stepWhatsappD"), to: "/reglages", testid: "onboarding-step-whatsapp" },
    { done: false, label: t("dash.stepShare"), desc: t("dash.stepShareD"), to: "/reglages", testid: "onboarding-step-share" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl bg-[#FFBA08]/12 border border-[#FFBA08]/40 p-5 mb-4"
      data-testid="onboarding-guide"
    >
      <div className="flex items-center gap-2 mb-1">
        <Rocket className="h-5 w-5 text-[#B07d00]" />
        <h3 className="font-display font-bold text-[#7a5800]">{t("dash.onboardingTitle")}</h3>
      </div>
      <p className="text-xs text-[#9a7400] mb-4">{t("dash.onboardingSub")}</p>
      <div className="space-y-2">
        {steps.map((s, i) => (
          <button
            key={s.testid}
            data-testid={s.testid}
            onClick={() => navigate(s.to)}
            className="w-full bg-white rounded-xl p-3 flex items-center gap-3 text-left active:scale-[0.99] transition-transform"
          >
            {s.done
              ? <CheckCircle2 className="h-6 w-6 text-[#2D6A4F] shrink-0" />
              : <span className="h-6 w-6 rounded-full bg-[#E85D04] text-white text-sm font-bold flex items-center justify-center shrink-0">{i + 1}</span>}
            <div className="flex-1 min-w-0">
              <p className={`font-semibold text-sm ${s.done ? "text-[#9c968a] line-through" : "text-[#1A1A1A]"}`}>{s.label}</p>
              <p className="text-xs text-[#9c968a]">{s.desc}</p>
            </div>
            <ChevronRight className="h-5 w-5 text-[#cfc8ba] shrink-0" />
          </button>
        ))}
      </div>
    </motion.div>
  );
}
